import { motion, useInView } from "framer-motion";
import { useRef } from "react";

interface AnimatedTextProps {
  lines: string[];
  className?: string;
  delay?: number;
  direction?: "up" | "down";
  as?: "h1" | "h2" | "h3" | "h4";
}

export function AnimatedText({ 
  lines, 
  className, 
  delay = 0, 
  direction = "up",
  as: Component = "h1"
}: AnimatedTextProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const initialY = direction === "up" ? 20 : -20;
  const animateY = 0;

  return (
    <Component
      ref={ref}
      className={`${className} text-balance`}
      style={{ perspective: "1000px", transformStyle: "preserve-3d" }}
    >
      {lines.map((line, lineIndex) => (
        <span key={lineIndex} className="block" style={{ transformStyle: "preserve-3d" }}>
          {line.split(" ").map((word, wordIndex) => (
            <motion.span
              key={`line${lineIndex}-${wordIndex}`}
              initial={{ filter: "blur(40px)", opacity: 0, translateY: initialY, rotateX: 20 }}
              animate={isInView ? { filter: "blur(0px)", opacity: 1, translateY: animateY, rotateX: 0 } : {}}
              transition={{
                duration: 0.9,
                ease: "easeOut",
                delay: delay + lineIndex * 0.1 + wordIndex * 0.1,
              }}
              // leading classes in tailwind:
              // - leading-none
              // - leading-tight
              // - leading-snug
              // - leading-normal
              // - leading-relaxed
              // - leading-loose
              className="inline-block bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50 leading-snug"
              style={{ paddingRight: "0.25em", transformStyle: "preserve-3d" }}
            >
              {word}
            </motion.span>
          ))}
        </span>
      ))}
    </Component>
  );
}
